import type { ApiResponse } from '../types/api';

const BASE_URL: string = (() => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return (import.meta.env as Record<string, string>).VITE_API_BASE_URL ?? '/api';
    }
  } catch {
    // Fallback for non-Vite environments (tests)
  }
  return '/api';
})();

let accessToken: string | null = localStorage.getItem('access_token');
let onUnauthorizedHandler: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) {
    localStorage.setItem('access_token', token);
  } else {
    localStorage.removeItem('access_token');
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setOnUnauthorized(handler: () => void): void {
  onUnauthorizedHandler = handler;
}

interface RequestOptions {
  method: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
}

function buildRequestUrl(path: string, params?: RequestOptions['params']): string {
  const normalizedBaseUrl = BASE_URL.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const isAbsoluteBaseUrl = /^https?:\/\//i.test(normalizedBaseUrl);
  const url = isAbsoluteBaseUrl
    ? new URL(`${normalizedBaseUrl}${normalizedPath}`)
    : new URL(`${normalizedBaseUrl}${normalizedPath}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return isAbsoluteBaseUrl ? url.toString() : `${url.pathname}${url.search}`;
}

export class ApiClientError extends Error {
  public readonly statusCode: number;
  public readonly errorCode: string;
  public readonly detail?: string;

  constructor(statusCode: number, errorCode: string, message: string, detail?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.detail = detail;
  }
}

async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const { body, params, headers: extraHeaders, method } = options;

  const url = buildRequestUrl(path, params);

  const headers: Record<string, string> = {
    ...extraHeaders,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const fetchInit: RequestInit = {
    method,
    headers,
  };

  if (body !== undefined) {
    headers['Content-Type'] ??= 'application/json';
    fetchInit.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchInit);

  if (response.status === 401) {
    onUnauthorizedHandler?.();
    throw new ApiClientError(401, 'unauthorized', 'Authentication required');
  }

  if (response.status === 403) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiClientError(
      403,
      payload?.error?.code ?? 'permission_denied',
      payload?.error?.message ?? 'Access denied',
      payload?.error?.detail,
    );
  }

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({}));
    throw new ApiClientError(
      response.status,
      errorPayload?.error?.code ?? 'unknown_error',
      errorPayload?.error?.message ?? `Request failed with status ${response.status}`,
      errorPayload?.error?.detail,
    );
  }

  const apiResponse: ApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiClientError(
      response.status,
      apiResponse.error?.code ?? 'unknown_error',
      apiResponse.error?.message ?? 'Request failed',
      apiResponse.error?.detail,
    );
  }

  return apiResponse.data as T;
}

async function requestEnvelope<T>(path: string, options: RequestOptions): Promise<ApiResponse<T>> {
  const { body, params, headers: extraHeaders, method } = options;

  const url = buildRequestUrl(path, params);

  const headers: Record<string, string> = {
    ...extraHeaders,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const fetchInit: RequestInit = {
    method,
    headers,
  };

  if (body !== undefined) {
    headers['Content-Type'] ??= 'application/json';
    fetchInit.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchInit);

  if (response.status === 401) {
    onUnauthorizedHandler?.();
    throw new ApiClientError(401, 'unauthorized', 'Authentication required');
  }

  if (response.status === 403) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiClientError(
      403,
      payload?.error?.code ?? 'permission_denied',
      payload?.error?.message ?? 'Access denied',
      payload?.error?.detail,
    );
  }

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({}));
    throw new ApiClientError(
      response.status,
      errorPayload?.error?.code ?? 'unknown_error',
      errorPayload?.error?.message ?? `Request failed with status ${response.status}`,
      errorPayload?.error?.detail,
    );
  }

  const apiResponse: ApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiClientError(
      response.status,
      apiResponse.error?.code ?? 'unknown_error',
      apiResponse.error?.message ?? 'Request failed',
      apiResponse.error?.detail,
    );
  }

  return apiResponse;
}

export const api = {
  get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return request<T>(path, { method: 'GET', params });
  },

  getEnvelope<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<ApiResponse<T>> {
    return requestEnvelope<T>(path, { method: 'GET', params });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' });
  },
};
